import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/partnerProgramPage.module.scss';
import detailStyles from '../css/detailPage.module.scss';
import axios from 'axios';
import { EnvPaths } from '../helpers/environment';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface EsimPurchaseDetail {
  _id: string;
  user_email: string;
  qr_code: string;
  sm_dp_address: string;
  activation_code: string;
  iccid: string;
  order_number: number;
  order_uuid: string;
  purchase_date: string;
  purchase_date_epoch: number;
  purchase_price: string;
  purchase_currency: string;
  final_price: string;
  discount_amount: string;
  discount_code: string;
  package_name: string;
  country_name: string;
  country_code: string;
  country_flag: string;
  country_flag_css: string;
  country_name_slug: string;
  is_region: boolean;
  new_customer: boolean;
  qr_code_image_base64: string;
}

const EsimPurchaseDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [purchaseData, setPurchaseData] = useState<EsimPurchaseDetail | null>(null);
  const [qrCodeVisible, setQrCodeVisible] = useState<boolean>(false);

  useEffect(() => {
    fetchPurchaseDetails();
  }, [id]);

  const fetchPurchaseDetails = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${EnvPaths.SERVER_URL}/admin/get-esim-purchase/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      setPurchaseData(response.data.data);
    } catch (error) {
      console.error('Error fetching purchase details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('esimAdmin.success.copiedToClipboard', { label }));
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error(t('esimAdmin.errors.copyFailed'));
    }
  };

  const handleDownloadQRCode = () => {
    if (!purchaseData?.qr_code_image_base64) return;

    const link = document.createElement('a');
    link.href = `data:image/png;base64,${purchaseData.qr_code_image_base64}`;
    link.download = `esim-qr-${purchaseData.iccid}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(t('esimAdmin.success.qrDownloaded'));
  };

  if (isLoading) {
    return (
      <main className={styles.pageContainer}>
        <div className={globalPageStyle.pageFlexContainer}>
          <div className="loader" />
        </div>
      </main>
    );
  }

  if (!purchaseData) {
    return (
      <main className={styles.pageContainer}>
        <div className={globalPageStyle.pageFlexContainer}>
          <div className={styles.pageContent}>
            <h1>{t('esimAdmin.purchaseNotFound')}</h1>
            <button className="primaryButton" onClick={() => navigate('/dashboard/esim-purchases')}>
              {t('esimAdmin.backToPurchases')}
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.pageContainer}>
      <div className={globalPageStyle.pageFlexContainer}>
        <div className={styles.pageContent}>
          {/* Header */}
          <div className={detailStyles.header}>
            <div className={detailStyles.headerLeft}>
              <img src="/assets/images/back-icon.svg" alt="Back" className={detailStyles.backIcon} onClick={() => navigate('/dashboard/esim-purchases')} />
              <div>
                <h1 className={detailStyles.title}>
                  {t('esimAdmin.purchaseDetails')} #{purchaseData.order_number}
                </h1>
                <p className={detailStyles.subtitle}>
                  {purchaseData.country_flag} {purchaseData.package_name}
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className={detailStyles.contentGrid}>
            {/* Left Column - Purchase Information */}
            <div className={detailStyles.section}>
              <h2 className={detailStyles.sectionTitle}>{t('esimAdmin.purchaseInformation')}</h2>
              <div className={detailStyles.infoGrid}>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.orderNumber')}</label>
                  <p>#{purchaseData.order_number}</p>
                </div>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.orderUUID')}</label>
                  <div className={detailStyles.copyable}>
                    <p className={detailStyles.monospace}>{purchaseData.order_uuid}</p>
                    <img src="/assets/images/copy-icon.svg" alt="Copy" onClick={() => handleCopyToClipboard(purchaseData.order_uuid, 'Order UUID')} className={detailStyles.copyIcon} />
                  </div>
                </div>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.customerEmail')}</label>
                  <p>{purchaseData.user_email}</p>
                </div>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.purchaseDate')}</label>
                  <p>{formatDate(purchaseData.purchase_date)}</p>
                </div>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.package')}</label>
                  <p>{purchaseData.package_name}</p>
                </div>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.country')}</label>
                  <p>
                    {purchaseData.country_flag} {purchaseData.country_name}
                  </p>
                </div>
                {/* <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.newCustomer')}</label>
                  <p>
                    <span className={purchaseData.new_customer ? detailStyles.badge : detailStyles.badgeGray}>
                      {purchaseData.new_customer ? t('esimAdmin.yes') : t('esimAdmin.no')}
                    </span>
                  </p>
                </div> */}
              </div>
            </div>

            {/* Right Column - Pricing Information */}
            <div className={detailStyles.section}>
              <h2 className={detailStyles.sectionTitle}>{t('esimAdmin.pricingInformation')}</h2>
              <div className={detailStyles.infoGrid}>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.originalPrice')}</label>
                  <p className={detailStyles.price}>
                    {purchaseData.purchase_currency}
                    {purchaseData.purchase_price}
                  </p>
                </div>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.discount')}</label>
                  <p className={detailStyles.discount}>{purchaseData.discount_amount !== '0.00' ? `-${purchaseData.purchase_currency}${purchaseData.discount_amount}` : t('esimAdmin.noDiscount')}</p>
                </div>
                {purchaseData.discount_code && (
                  <div className={detailStyles.infoItem}>
                    <label>{t('esimAdmin.discountCode')}</label>
                    <p className={detailStyles.discountCode}>{purchaseData.discount_code}</p>
                  </div>
                )}
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.finalPrice')}</label>
                  <p className={detailStyles.finalPrice}>
                    {purchaseData.purchase_currency}
                    {purchaseData.final_price}
                  </p>
                </div>
              </div>
            </div>

            {/* Activation Details */}
            <div className={detailStyles.sectionFull}>
              <h2 className={detailStyles.sectionTitle}>{t('esimAdmin.activationDetails')}</h2>
              <div className={detailStyles.infoGrid}>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.iccid')}</label>
                  <div className={detailStyles.copyable}>
                    <p className={detailStyles.monospace}>{purchaseData.iccid}</p>
                    <img src="/assets/images/copy-icon.svg" alt="Copy" onClick={() => handleCopyToClipboard(purchaseData.iccid, 'ICCID')} className={detailStyles.copyIcon} />
                  </div>
                </div>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.smdpAddress')}</label>
                  <div className={detailStyles.copyable}>
                    <p className={detailStyles.monospace}>{purchaseData.sm_dp_address}</p>
                    <img src="/assets/images/copy-icon.svg" alt="Copy" onClick={() => handleCopyToClipboard(purchaseData.sm_dp_address, 'SM-DP+ Address')} className={detailStyles.copyIcon} />
                  </div>
                </div>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.activationCode')}</label>
                  <div className={detailStyles.copyable}>
                    <p className={detailStyles.monospace}>{purchaseData.activation_code}</p>
                    <img src="/assets/images/copy-icon.svg" alt="Copy" onClick={() => handleCopyToClipboard(purchaseData.activation_code, 'Activation Code')} className={detailStyles.copyIcon} />
                  </div>
                </div>
                <div className={detailStyles.infoItem}>
                  <label>{t('esimAdmin.qrCodeString')}</label>
                  <div className={detailStyles.copyable}>
                    <p className={detailStyles.monospace}>{purchaseData.qr_code}</p>
                    <img src="/assets/images/copy-icon.svg" alt="Copy" onClick={() => handleCopyToClipboard(purchaseData.qr_code, 'QR Code')} className={detailStyles.copyIcon} />
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code Section */}
            <div className={detailStyles.sectionFull}>
              <h2 className={detailStyles.sectionTitle}>{t('esimAdmin.qrCode')}</h2>
              <div className={detailStyles.qrCodeContainer}>
                {purchaseData.qr_code_image_base64 ? (
                  <>
                    {qrCodeVisible && (
                      <div className={detailStyles.qrCodeWrapper}>
                        <img src={`data:image/png;base64,${purchaseData.qr_code_image_base64}`} alt="eSIM QR Code" className={detailStyles.qrCodeImage} />
                      </div>
                    )}
                    <div className={detailStyles.qrCodeActions}>
                      <button className="primaryButton inverse" onClick={() => setQrCodeVisible(!qrCodeVisible)}>
                        {qrCodeVisible ? t('esimAdmin.hideQR') : t('esimAdmin.showQR')}
                      </button>
                      <button className="primaryButton" onClick={handleDownloadQRCode}>
                        {t('esimAdmin.downloadQR')}
                      </button>
                    </div>
                  </>
                ) : (
                  <p>{t('esimAdmin.noQRCode')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default EsimPurchaseDetails;
